/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';

/**
 * SetGraphDefaultService
 * Uses the legacy PATCH /graphs modify payload to set a specific (graphId, version) as default.
 *
 * Payload:
 * - graphId
 * - version
 * - tenantId (used for x-berlin-tenant-id header via buildRequestPartsFromPayload)
 */
export default class SetGraphDefaultService extends BaseService {
  constructor() {
    super(constants.SET_GRAPH_DEFAULT, 'setGraphDefaultStatus');
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { graphId, version, requestId } = payload;

    const requestData = {
      operation: 'modify',
      criteria: {
        graphId,
        version
      },
      properties: {
        isDefault: true
      }
    };

    // Keep requestId available for response correlation (not sent to backend)
    this.requestId = requestId;
    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').patch('/graphs', requestData, { headers: enhancedHeaders });
    }
    return this.getClient('aiadmin').patch('/graphs', requestData, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    if (data && data.success) {
      Framework.getEventManager().publish(constants.SET_GRAPH_DEFAULT_SUCCESS, { data, requestId: this.requestId });
    } else {
      Framework.getEventManager().publish(constants.SET_GRAPH_DEFAULT_ERROR, {
        message: data?.message || 'Failed',
        requestId: this.requestId
      });
    }
  }

  handleError(error) {
    const { response } = error;
    if (response) {
      const { status } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
    }
    Framework.getEventManager().publish(constants.SET_GRAPH_DEFAULT_ERROR, {
      message: error?.message || 'Failed',
      requestId: this.requestId
    });
  }
}
